import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import Header from '../components/layout/Header';
import { NativeAdCard } from '../components/ads/AdComponents';
import { 
  Trophy, Video, ExternalLink, Clock, Gamepad2, Search, 
  Filter, SortDesc, Grid3X3, List, Play, User, Calendar,
  ChevronDown, X, Sparkles, Eye, Heart, Share2, Flame, 
  TrendingUp, Zap, Star, Crown
} from 'lucide-react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { handleTwitchLogin } from '../utils/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hero Stats Card
const StatCard = ({ icon: Icon, value, label, color }) => (
  <div className={`relative p-4 rounded-xl bg-gradient-to-br ${color} border border-white/10 overflow-hidden group hover:scale-105 transition-transform`}>
    <div className="absolute top-0 right-0 w-20 h-20 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
    <Icon className="w-6 h-6 text-white/80 mb-2" />
    <div className="text-2xl font-black text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>{value}</div>
    <div className="text-xs text-white/60 uppercase tracking-wider">{label}</div>
  </div>
);

// Featured Submission Card (Large)
const FeaturedSubmissionCard = ({ submission }) => {
  const thumbnail = getVideoThumbnail(submission.video_url, submission.video_type);
  
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className="relative group"
    >
      <div className="absolute -inset-1 bg-gradient-to-r from-red-500 via-purple-500 to-red-500 rounded-2xl blur-lg opacity-30 group-hover:opacity-50 transition-opacity" />
      <Card className="relative bg-zinc-900 border-0 overflow-hidden rounded-2xl">
        <div className="grid md:grid-cols-2 gap-0">
          {/* Thumbnail */}
          <div className="relative aspect-video md:aspect-auto">
            {thumbnail ? (
              <img src={thumbnail} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full min-h-[200px] bg-gradient-to-br from-purple-900/50 to-red-900/50 flex items-center justify-center">
                <Play className="w-16 h-16 text-white/30" />
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            
            {/* Play Button */}
            <a 
              href={submission.video_url}
              target="_blank"
              rel="noopener noreferrer"
              className="absolute inset-0 flex items-center justify-center"
            >
              <div className="w-20 h-20 rounded-full bg-red-500/90 flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-2xl">
                <Play className="w-10 h-10 text-white ml-1" fill="currentColor" />
              </div>
            </a>

            {/* Featured Badge */}
            <div className="absolute top-4 left-4 flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full">
              <Crown className="w-4 h-4 text-white" />
              <span className="text-xs font-bold text-white">FEATURED</span>
            </div>

            {/* Platform Badge */}
            <div className={`absolute top-4 right-4 px-3 py-1.5 rounded-full text-xs font-bold ${
              submission.video_type === 'twitch' ? 'bg-purple-500' : 'bg-red-500'
            }`}>
              {submission.video_type === 'twitch' ? '📺 Twitch' : '▶️ YouTube'}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 flex flex-col justify-between">
            <div>
              <h3 className="text-xl font-bold text-white mb-3 line-clamp-2">
                {submission.video_title || 'Epic Gaming Moment'}
              </h3>
              
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src={submission.profile_image_url || 'https://via.placeholder.com/40'}
                  alt=""
                  className="w-10 h-10 rounded-full border-2 border-purple-500"
                />
                <div>
                  <p className="font-semibold text-white">{submission.display_name}</p>
                  <p className="text-sm text-gray-500">@{submission.twitch_username}</p>
                </div>
              </div>

              {submission.challenge_title && (
                <div className="flex items-center gap-2 px-3 py-2 bg-purple-500/20 rounded-lg mb-4">
                  <Trophy className="w-4 h-4 text-purple-400" />
                  <span className="text-sm text-purple-300">{submission.challenge_title}</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">
                {getTimeAgo(submission.created_at)}
              </span>
              <a 
                href={submission.video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-red-500 hover:bg-red-600 rounded-lg text-white text-sm font-semibold transition-colors"
              >
                Watch Now <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

// Enhanced Filter Toolbar
const FilterToolbar = ({ 
  searchTerm, setSearchTerm, selectedChallenge, setSelectedChallenge,
  sortOrder, setSortOrder, viewMode, setViewMode, challenges, totalCount
}) => {
  return (
    <div className="sticky top-16 z-30 backdrop-blur-xl bg-black/90 border-b border-white/10 -mx-3 px-3 sm:-mx-4 sm:px-4 py-4 mb-8">
      <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
        {/* Search */}
        <div className="relative flex-1 max-w-md w-full">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            placeholder="Search submissions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500/50 focus:ring-2 focus:ring-purple-500/20 transition-all"
            data-testid="gallery-search"
          />
          {searchTerm && (
            <button 
              onClick={() => setSearchTerm('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-white hover:bg-white/10 rounded-full transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-3 items-center">
          {/* Challenge Filter */}
          <div className="relative">
            <select
              value={selectedChallenge}
              onChange={(e) => setSelectedChallenge(e.target.value)}
              className="appearance-none pl-4 pr-10 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-purple-500/50 cursor-pointer min-w-[180px] hover:bg-white/10 transition-colors"
              data-testid="challenge-filter"
            >
              <option value="all">All Challenges</option>
              {challenges.map(c => (
                <option key={c.id} value={c.id}>{c.title}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
          </div>

          {/* Sort Order */}
          <div className="relative">
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="appearance-none pl-4 pr-10 py-3 bg-white/5 border border-white/10 rounded-xl text-sm text-white focus:outline-none focus:border-purple-500/50 cursor-pointer min-w-[150px] hover:bg-white/10 transition-colors"
              data-testid="sort-filter"
            >
              <option value="newest">🕐 Newest</option>
              <option value="oldest">📅 Oldest</option>
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
          </div>

          {/* View Toggle */}
          <div className="flex items-center bg-white/5 border border-white/10 rounded-xl p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2.5 rounded-lg transition-all ${viewMode === 'grid' ? 'bg-purple-500 text-white' : 'text-gray-500 hover:text-white hover:bg-white/10'}`}
              data-testid="view-grid"
            >
              <Grid3X3 className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2.5 rounded-lg transition-all ${viewMode === 'list' ? 'bg-purple-500 text-white' : 'text-gray-500 hover:text-white hover:bg-white/10'}`}
              data-testid="view-list"
            >
              <List className="w-4 h-4" />
            </button>
          </div>

          {/* Results Count */}
          <div className="hidden sm:flex items-center gap-2 px-4 py-2 bg-white/5 rounded-xl">
            <Video className="w-4 h-4 text-purple-400" />
            <span className="text-sm text-white font-medium">{totalCount}</span>
            <span className="text-sm text-gray-500">videos</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Submission Card - Grid View (Improved)
const SubmissionCardGrid = ({ submission, index }) => {
  const thumbnail = getVideoThumbnail(submission.video_url, submission.video_type);
  const timeAgo = getTimeAgo(submission.created_at);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      whileHover={{ y: -8 }}
    >
      <Card 
        className="group bg-zinc-900/80 border-white/5 overflow-hidden hover:border-purple-500/50 transition-all duration-300 rounded-xl hover:shadow-[0_0_40px_rgba(168,85,247,0.15)]"
        data-testid={`submission-card-${submission.id}`}
      >
        {/* Thumbnail */}
        <div className="relative aspect-video bg-black overflow-hidden">
          {thumbnail ? (
            <img 
              src={thumbnail} 
              alt=""
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
            />
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900">
              {submission.video_type === 'twitch' ? (
                <svg className="w-14 h-14 text-purple-500/40" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
                </svg>
              ) : (
                <svg className="w-14 h-14 text-red-500/40" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              )}
            </div>
          )}
          
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent opacity-60" />
          
          {/* Play Button */}
          <a 
            href={submission.video_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300"
          >
            <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center transform scale-50 group-hover:scale-100 transition-transform duration-300 border border-white/30">
              <Play className="w-8 h-8 text-white ml-1" fill="currentColor" />
            </div>
          </a>

          {/* Platform Badge */}
          <div className={`absolute top-3 left-3 px-2.5 py-1 rounded-lg text-xs font-bold backdrop-blur-md ${
            submission.video_type === 'twitch' 
              ? 'bg-purple-500/90 text-white' 
              : 'bg-red-500/90 text-white'
          }`}>
            {submission.video_type === 'twitch' ? '📺 Twitch' : '▶️ YouTube'}
          </div>

          {/* Time Badge */}
          <div className="absolute bottom-3 right-3 px-2.5 py-1 rounded-lg text-xs bg-black/70 text-white backdrop-blur-sm font-medium">
            {timeAgo}
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          <h3 className="font-bold text-white mb-3 line-clamp-2 group-hover:text-purple-400 transition-colors text-sm leading-snug">
            {submission.video_title || 'Untitled Submission'}
          </h3>
          
          {/* User Info */}
          <div className="flex items-center gap-3">
            <div className="relative">
              <img 
                src={submission.profile_image_url || 'https://via.placeholder.com/32'} 
                alt=""
                className="w-8 h-8 rounded-full border-2 border-purple-500/50"
              />
              <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-zinc-900" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-white font-medium truncate">{submission.display_name}</p>
              {submission.challenge_title && (
                <p className="text-xs text-purple-400 truncate">{submission.challenge_title}</p>
              )}
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

// Submission Card - List View (Improved)
const SubmissionCardList = ({ submission, index }) => {
  const thumbnail = getVideoThumbnail(submission.video_url, submission.video_type);
  const { dateStr, timeStr } = formatDateTime(submission.created_at);

  return (
    <motion.div
      initial={{ opacity: 0, x: -30 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: index * 0.03 }}
    >
      <Card 
        className="group bg-zinc-900/80 border-white/5 overflow-hidden hover:border-purple-500/30 transition-all duration-300 hover:bg-zinc-800/80 rounded-xl"
        data-testid={`submission-card-list-${submission.id}`}
      >
        <div className="flex items-center gap-4 p-4">
          {/* Thumbnail */}
          <a 
            href={submission.video_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="relative w-48 aspect-video bg-black rounded-xl overflow-hidden flex-shrink-0 group/thumb"
          >
            {thumbnail ? (
              <img src={thumbnail} alt="" className="w-full h-full object-cover group-hover/thumb:scale-105 transition-transform duration-500" />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-zinc-800 to-zinc-900">
                <Play className="w-10 h-10 text-white/30" />
              </div>
            )}
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover/thumb:opacity-100 transition-opacity flex items-center justify-center">
              <Play className="w-10 h-10 text-white" fill="currentColor" />
            </div>
            <div className={`absolute top-2 left-2 px-2 py-0.5 rounded-md text-[10px] font-bold ${
              submission.video_type === 'twitch' ? 'bg-purple-500' : 'bg-red-500'
            }`}>
              {submission.video_type === 'twitch' ? 'Twitch' : 'YouTube'}
            </div>
          </a>

          {/* Info */}
          <div className="flex-1 min-w-0 py-1">
            <h3 className="font-bold text-white mb-2 line-clamp-1 group-hover:text-purple-400 transition-colors">
              {submission.video_title || 'Untitled Submission'}
            </h3>
            <div className="flex items-center gap-3 mb-2">
              <img 
                src={submission.profile_image_url || 'https://via.placeholder.com/24'} 
                alt=""
                className="w-6 h-6 rounded-full border border-purple-500/50"
              />
              <span className="text-sm text-gray-300">{submission.display_name}</span>
              {submission.challenge_title && (
                <>
                  <span className="text-gray-600">•</span>
                  <span className="text-sm text-purple-400 flex items-center gap-1">
                    <Trophy className="w-3 h-3" />
                    {submission.challenge_title}
                  </span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-500">
              <Clock className="w-3 h-3" />
              <span>{dateStr} at {timeStr}</span>
            </div>
          </div>

          {/* Action */}
          <a 
            href={submission.video_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-purple-500/20 hover:bg-purple-500 text-purple-400 hover:text-white rounded-lg transition-all text-sm font-medium"
          >
            Watch <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </Card>
    </motion.div>
  );
};

// Challenge Section Header (Improved)
const ChallengeSectionHeader = ({ title, gameName, count, isExpanded, onToggle }) => {
  return (
    <button 
      onClick={onToggle}
      className="w-full flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-purple-500/10 to-red-500/10 border border-white/10 hover:border-purple-500/30 transition-all group"
    >
      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-red-500 flex items-center justify-center flex-shrink-0">
        <Trophy className="w-6 h-6 text-white" />
      </div>
      <div className="flex-1 text-left min-w-0">
        <h2 className="text-lg font-bold text-white group-hover:text-purple-400 transition-colors truncate" style={{ fontFamily: 'Orbitron, sans-serif' }}>
          {title}
        </h2>
        <div className="flex items-center gap-3 text-sm mt-1">
          {gameName && (
            <span className="flex items-center gap-1.5 text-purple-400">
              <Gamepad2 className="w-4 h-4" />
              {gameName}
            </span>
          )}
          <span className="flex items-center gap-1.5 text-gray-500">
            <Video className="w-4 h-4" />
            {count} video{count !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
      <ChevronDown className={`w-6 h-6 text-gray-500 transition-transform duration-300 ${isExpanded ? 'rotate-180' : ''}`} />
    </button>
  );
};

// Helper Functions
const getVideoThumbnail = (url, type) => {
  if (type === 'youtube') {
    let videoId = '';
    if (url.includes('youtu.be/')) {
      videoId = url.split('youtu.be/')[1]?.split('?')[0];
    } else if (url.includes('watch?v=')) {
      videoId = url.split('watch?v=')[1]?.split('&')[0];
    }
    if (videoId) return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  }
  return null;
};

const formatDateTime = (dateString) => {
  const date = new Date(dateString);
  return {
    dateStr: date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    timeStr: date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  };
};

const getTimeAgo = (dateString) => {
  const seconds = Math.floor((new Date() - new Date(dateString)) / 1000);
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return `${Math.floor(seconds / 604800)}w ago`;
};

// Main Gallery Component
const Gallery = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedChallenge, setSelectedChallenge] = useState('all');
  const [sortOrder, setSortOrder] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');
  const [expandedSections, setExpandedSections] = useState({});

  useEffect(() => {
    const fetchSubmissions = async () => {
      try {
        const response = await axios.get(`${API}/submissions`);
        const safeData = Array.isArray(response.data) ? response.data : [];
        setSubmissions(safeData);
        // Initialize all sections as collapsed (false)
        const sections = {};
        safeData.forEach(sub => { sections[sub.challenge_id || 'uncategorized'] = false; });
        setExpandedSections(sections);
      } catch (error) {
        console.error('Failed to fetch submissions:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSubmissions();
  }, []);

  const safeSubmissionsList = Array.isArray(submissions) ? submissions : [];

  const challenges = useMemo(() => {
    const seen = new Map();
    safeSubmissionsList.forEach(sub => {
      if (sub.challenge_id && !seen.has(sub.challenge_id)) {
        seen.set(sub.challenge_id, { id: sub.challenge_id, title: sub.challenge_title || 'Unknown Challenge' });
      }
    });
    return Array.from(seen.values());
  }, [safeSubmissionsList]);

  const filteredSubmissions = useMemo(() => {
    let result = [...safeSubmissionsList];
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(s => 
        s.video_title?.toLowerCase().includes(term) ||
        s.display_name?.toLowerCase().includes(term) ||
        s.challenge_title?.toLowerCase().includes(term)
      );
    }
    if (selectedChallenge !== 'all') result = result.filter(s => s.challenge_id === selectedChallenge);
    result.sort((a, b) => {
      const dateA = new Date(a.created_at), dateB = new Date(b.created_at);
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });
    return result;
  }, [safeSubmissionsList, searchTerm, selectedChallenge, sortOrder]);

  const groupedSubmissions = useMemo(() => {
    const groups = {};
    filteredSubmissions.forEach(sub => {
      const key = sub.challenge_id || 'uncategorized';
      if (!groups[key]) groups[key] = { title: sub.challenge_title || 'General Submissions', game_name: sub.game_name, submissions: [] };
      groups[key].submissions.push(sub);
    });
    return groups;
  }, [filteredSubmissions]);

  const featuredSubmission = filteredSubmissions[0];
  const remainingSubmissions = filteredSubmissions.slice(1);

  const handleLogin = handleTwitchLogin;

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-zinc-950 to-black">
      <Header />

      {/* Animated Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-purple-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-red-500/5 rounded-full blur-3xl" />
      </div>

      <main className="relative pt-24 pb-12 px-3 sm:px-4 max-w-7xl mx-auto">
        {/* Hero Section */}
        <div className="mb-10">
          <div className="flex flex-col lg:flex-row justify-between gap-8">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-xl">
                  <Play className="w-7 h-7 text-red-500" />
                </div>
                <div>
                  <h1 className="text-3xl sm:text-4xl font-black text-white" style={{ fontFamily: 'Orbitron, sans-serif' }} data-testid="gallery-title">
                    VIDEO GALLERY
                  </h1>
                  <p className="text-gray-500">Community submissions & epic moments</p>
                </div>
              </div>
            </div>
            
            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <StatCard icon={Play} value={submissions.length} label="Videos" color="from-red-500/20 to-red-600/20" />
              <StatCard icon={Trophy} value={challenges.length} label="Challenges" color="from-red-500/20 to-red-600/20" />
              <StatCard icon={User} value={new Set(submissions.map(s => s.user_id)).size} label="Creators" color="from-red-500/20 to-red-600/20" />
            </div>
          </div>
        </div>

        {/* Filter Toolbar */}
        <FilterToolbar
          searchTerm={searchTerm} setSearchTerm={setSearchTerm}
          selectedChallenge={selectedChallenge} setSelectedChallenge={setSelectedChallenge}
          sortOrder={sortOrder} setSortOrder={setSortOrder}
          viewMode={viewMode} setViewMode={setViewMode}
          challenges={challenges} totalCount={filteredSubmissions.length}
        />

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="relative w-16 h-16">
              <div className="absolute inset-0 border-4 border-purple-500/30 rounded-full" />
              <div className="absolute inset-0 border-4 border-transparent border-t-purple-500 rounded-full animate-spin" />
            </div>
            <p className="text-gray-500 mt-4">Loading submissions...</p>
          </div>
        ) : filteredSubmissions.length === 0 ? (
          <Card className="bg-zinc-900/50 border-white/5 p-16 text-center rounded-2xl">
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-purple-500/20 to-red-500/20 rounded-full flex items-center justify-center">
              <Video className="w-10 h-10 text-gray-600" />
            </div>
            <h3 className="text-2xl font-bold mb-3 text-white" style={{ fontFamily: 'Orbitron, sans-serif' }}>
              {searchTerm || selectedChallenge !== 'all' ? 'No Results Found' : 'No Submissions Yet'}
            </h3>
            <p className="text-gray-500 mb-8 max-w-md mx-auto">
              {searchTerm || selectedChallenge !== 'all' 
                ? 'Try adjusting your search or filters'
                : 'Be the first to share your epic gaming moment!'}
            </p>
            {!searchTerm && selectedChallenge === 'all' && (
              <Button 
                className="bg-gradient-to-r from-purple-500 to-red-500 hover:from-purple-600 hover:to-red-600 px-8 py-3"
                onClick={isAuthenticated ? () => navigate('/submit') : handleLogin}
              >
                <Zap className="w-5 h-5 mr-2" />
                {isAuthenticated ? 'Submit Your Video' : 'Login to Submit'}
              </Button>
            )}
          </Card>
        ) : (
          <div className="space-y-10">
            {/* Featured Submission */}
            {featuredSubmission && searchTerm === '' && selectedChallenge === 'all' && (
              <div className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <Flame className="w-5 h-5 text-orange-400" />
                  <span className="text-sm font-bold text-orange-400 uppercase tracking-wider">Latest Submission</span>
                </div>
                <FeaturedSubmissionCard submission={featuredSubmission} />
              </div>
            )}

            {/* Grouped Submissions */}
            {Object.entries(groupedSubmissions).map(([challengeId, group]) => (
              <div key={challengeId} className="space-y-4">
                <ChallengeSectionHeader
                  title={group.title}
                  gameName={group.game_name}
                  count={group.submissions.length}
                  isExpanded={expandedSections[challengeId] !== false}
                  onToggle={() => setExpandedSections(prev => ({ ...prev, [challengeId]: !prev[challengeId] }))}
                />

                <AnimatePresence>
                  {expandedSections[challengeId] === true && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      {viewMode === 'grid' ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                          {group.submissions.map((submission, index) => (
                            <React.Fragment key={submission.id}>
                              <SubmissionCardGrid submission={submission} index={index} />
                              {(index + 1) % 8 === 0 && index < group.submissions.length - 1 && (
                                <NativeAdCard key={`ad-${index}`} />
                              )}
                            </React.Fragment>
                          ))}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {group.submissions.map((submission, index) => (
                            <SubmissionCardList key={submission.id} submission={submission} index={index} />
                          ))}
                        </div>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default Gallery;
